import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

import ChannelGroup from "../components/ChannelGroup";
import PostGroup from "../components/PostGroup";
import MultiMessageModal from "../components/MultiMessageModal";
import ConfirmModal from "../components/ConfirmModal";
import NewChannelModal from "../components/NewChannelModal";
import EditChannelModal from "../components/EditChannelModal";
import PageWrapper from "../components/PageWrapper";

import useAlert from "../hooks/use-alert";

import Channel from "../typings/Channel";

const ChannelsPage = () => {
  const [posts, setPosts] = useState<Channel["messages"]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeChannelId, setActiveChannelId] = useState("");
  const [activeChannelName, setActiveChannelName] = useState("");

  const [createChannelModal, setCreateChannelModal] = useState(false);
  const [multiMessageModal, setMultiMessageModal] = useState(false);
  const [deleteChannelModal, setDeleteChannelModal] = useState(false);
  const [editChannelModal, setEditChannelModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const { alert, alertMessage, closeAlert, showAlert } = useAlert(setIsLoading);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const res = await axios.get<Channel[]>("/api/dashboardUser/channels");
        setChannels(res.data);
      } catch (error) {
        showAlert("Error while fetching data. Try again.", "danger");
      }
    };

    fetchChannels();
  }, [showAlert]);

  const setActiveChannel = (id = "", name = "") => {
    setActiveChannelId(id);
    setActiveChannelName(name);
  };

  const editChannelHandler = useCallback(
    async (name: string, description: string, owners: Channel["owners"]) => {
      setIsLoading(true);
      setEditChannelModal(false);
      try {
        const res = await axios.patch<Channel[]>(
          `/api/channels/${activeChannelId}`,
          {
            name: `#${name.trim()}`,
            description: description,
            owners: owners,
          }
        );

        setChannels(res.data);
        showAlert("Channel edited successfully", "success");
      } catch (err) {
        showAlert(
          "Error while updating the channel data. Try again.",
          "danger"
        );
      }
    },
    [activeChannelId, showAlert]
  );

  const createChannelHandler = useCallback(
    async (
      channelName: string,
      channelDesc: string,
      coOwners: Channel["owners"]
    ) => {
      setIsLoading(true);
      setCreateChannelModal(false);
      try {
        let channel;

        if (coOwners.length !== 0) {
          channel = {
            name: `#${channelName.trim()}`,
            description: channelDesc,
            coOwners: coOwners,
          };
        } else {
          channel = {
            name: `#${channelName.trim()}`,
            description: channelDesc,
          };
        }

        const res = await axios.post<Channel[]>(`/api/channels`, channel);

        setChannels(res.data);
        showAlert("Channel added successfully", "success");
      } catch (err) {
        showAlert(
          "Error while creating the channel. Invalid parameters or a channel with the same name already exists. Try again.",
          "danger"
        );
      }
    },
    [showAlert]
  );

  const deleteChannelHandler = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.delete<Channel[]>(
        `/api/channels/deleteById/${activeChannelId}`
      );

      setDeleteChannelModal(false);
      setActiveChannel();
      setPosts([]);
      setChannels(res.data);
      showAlert("Channel deleted successfully", "success");
    } catch {
      showAlert("Error while deleting the channel. Try again.", "danger");
    }
  }, [activeChannelId, showAlert]);

  const onClickChannelHandler = useCallback(
    async (channel: Channel) => {
      if (channel._id !== activeChannelId) {
        setMessagesLoading(true);
        setActiveChannel(channel._id, channel.name);
        try {
          const res = await axios.get<Channel["messages"]>(
            `/api/channels/messages/findById/${channel._id}`
          );

          setPosts(res.data);
          setMessagesLoading(false);
        } catch (err) {
          showAlert(
            "Error while fetching channels' messages. Try again.",
            "danger"
          );
        }
      }
    },
    [activeChannelId, showAlert]
  );

  const sendMessageHandler = useCallback(
    async (message: string, tags = [activeChannelName]) => {
      setIsLoading(true);
      try {
        const body = {
          message: message,
          tags: tags,
          channelId: activeChannelId,
        };

        const res = await axios.post<{ messages: Channel["messages"] }>(
          `/api/channels/publish`,
          body
        );

        setPosts(res.data.messages);
        setIsLoading(false);
      } catch (err) {
        showAlert("Error while sending messages. Try again.", "danger");
      }
    },
    [activeChannelId, activeChannelName, showAlert]
  );

  return (
    <>
      <NewChannelModal
        show={createChannelModal}
        onClose={setCreateChannelModal}
        onCreateChannelHandler={createChannelHandler}
      />

      {activeChannelId && (
        <EditChannelModal
          show={editChannelModal}
          onClose={setEditChannelModal}
          onEditChannelHandler={editChannelHandler}
          channel={channels.find((i) => i._id === activeChannelId)!} // "!" non-null assetion operator
        />
      )}

      <MultiMessageModal
        show={multiMessageModal}
        onClose={setMultiMessageModal}
        onSendMessageHandler={sendMessageHandler}
        channels={useMemo(() => channels, [channels])}
      />

      <ConfirmModal
        title="Delete channel"
        text={useMemo(
          () => (
            <p>
              Are you sure you want to delete channel <b>{activeChannelName}</b>
              ?
            </p>
          ),
          [activeChannelName]
        )}
        show={deleteChannelModal}
        onClose={setDeleteChannelModal}
        onClick={deleteChannelHandler}
      />

      <PageWrapper
        isLoading={isLoading}
        loadingText="For channels with a large number of subscribers, the operation may take a few minutes. Please wait."
        alert={alert}
        alertMessage={alertMessage}
        onClose={closeAlert}
      >
        <ChannelGroup
          channels={useMemo(() => channels, [channels])}
          activeChannelId={activeChannelId}
          onClickChannelHandler={onClickChannelHandler}
          setDeleteChannelModal={setDeleteChannelModal}
          setEditChannelModal={setEditChannelModal}
          setCreateChannelModal={setCreateChannelModal}
        />
        <PostGroup
          loading={messagesLoading}
          channelName={activeChannelName}
          posts={useMemo(() => posts, [posts])}
          onSendMessageHandler={sendMessageHandler}
          setMultiMessageModal={setMultiMessageModal}
        />
      </PageWrapper>
    </>
  );
};

export default ChannelsPage;
